
import {Directive, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, Output, Renderer2, DOCUMENT} from '@angular/core';
import {distinctUntilChanged, filter, map, switchMap, takeUntil, tap,} from 'rxjs/operators';
import {fromEvent, Subject} from 'rxjs';
import {TileDataService} from "../services/tile_data.service";
import {ColumnFormats} from "../services/table-utils.service";
import {CustomDialogService} from "../services/custom-dialog.service";

@Directive({
    selector: '[rowResizable]'
})
export class RowResizeDirective implements OnInit, OnDestroy {
    private readonly onDestroy = new Subject<void>();

    @Input('rowResizable') formats: Partial<ColumnFormats>;
    editing = false;
    handle: Element;

    @HostListener('click', ['$event'])
    clickEvent(event) {
        if (!this.editing || !this.formats.allow_resize || this.dragging) return;
        event.preventDefault();
        event.stopPropagation();
        this.openModal(event);
    }

    dragging = false;
    @Output()
    readonly rowResizable = fromEvent<MouseEvent>(
        this.elementRef.nativeElement,
        'mousedown'
    ).pipe(
        filter(event => event['which'] === 1 && this.editing),
        tap(e => {
            e.preventDefault();
            e.stopPropagation();
        }),
        switchMap((event) => {
            this.removeLimits();
            const {height, bottom} = this.elementRef.nativeElement
                .getBoundingClientRect();
            return fromEvent<MouseEvent>(this.documentRef, 'mousemove').pipe(
                map(({clientY}) => {
                    this.dragging = true;
                    return height + clientY - bottom
                }),
                tap(() => this.setLimits(true)),
                distinctUntilChanged(),
                takeUntil((fromEvent(this.documentRef, 'mouseup') ||
                        fromEvent(this.elementRef.nativeElement.closest('thead'), 'mouseout')).pipe(
                        tap((e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        })
                    )
                )
            );
        }),
        tap(() => this.dragging = false),
        takeUntil(this.onDestroy)
    );

    constructor(
        @Inject(DOCUMENT) private readonly documentRef: Document,
        @Inject(ElementRef)
        private readonly elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2, private tileData: TileDataService,
                private customDialogService: CustomDialogService,
    ) {
        this.handle = this.renderer.createElement('div');
        this.renderer.addClass(this.handle, 'resize-row-handle');
        const grip = this.renderer.createElement('i');
        this.renderer.addClass(grip, 'fas')
        this.renderer.addClass(grip, 'fa-grip-horizontal')
        this.renderer.appendChild(this.handle, grip);
        this.tileData.editing.pipe(takeUntil(this.onDestroy))
            .subscribe((on: boolean) => {
                this.editing = on;
                this.toggleResizeHandles(on)
            })
    }

    ngOnInit(): void {
        this.setLimits(true);
    }

    //TODO Why does mat-table ignore these when resizing another column
    setLimits(on) {
        if (!this.formats || !this.formats.height || (!this.formats.allow_resize && this.editing)) return;
        this.renderer.setStyle(this.elementRef.nativeElement, 'height', this.formats.height + 'px');

    }

    removeLimits() {
        if (!this.formats || !this.formats.height || !this.editing || (!this.formats.allow_resize && this.editing)) return;
        this.renderer.removeStyle(this.elementRef.nativeElement, 'max-height');
        this.renderer.removeStyle(this.elementRef.nativeElement, 'min-height');
    }

    toggleResizeHandles(on = false) {
        /* Just displays and hides the resizing handles on Edit Layout */
        if (this.formats && this.formats.allow_resize !== true) return;
        if (on) {
            this.renderer.removeChild(this.elementRef.nativeElement, this.handle);
            this.renderer.appendChild(this.elementRef.nativeElement, this.handle)
        } else {
            this.renderer.removeChild(this.elementRef.nativeElement, this.handle);
        }
    }

    openModal(event: any) {
        const config = {
            component: 'ColumnWidth',
            parameters: {width: this.formats.height, title: 'Row Height (px)'},
            position: {top: 2, left: 60, max_right: 200},
        }
        const setStyles: (event) => void = (event) => {
            this.formats.height = event;
            this.setLimits(true);
        }
        const panelClass = 'series-summary-menu-dialog';
        this.customDialogService.openCustomDialog(event, config, panelClass, setStyles);
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
